import React, { useMemo, useState } from 'react';
import { Button, Radio, Skeleton } from 'antd';
import { CloseOutlined, BarChartOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
    GET_INHUB_SORTING_BAGGING_DEST_DRILLDOWN,
    type GetInhubSortingBaggingDestDrilldownResponse
} from '../graphql/queries/getInhubSortingBaggingDestDrilldown';
import type { InhubBaggingDestBucket } from '../graphql/types';

interface Props {
    open: boolean;
    onClose: () => void;
    hubId: number;
    rangeStart: string;
    rangeEnd: string;
}

const I18N = 'wms.delivery.inhubDashboard.bagging';
const HUB_BAR_COLOR = '#c4b5fd';
const WHS_BAR_COLOR = '#86efac';
const HUB_LABEL_COLOR = '#7e22ce';
const WHS_LABEL_COLOR = '#15803d';

const BaggingDrilldownPanel: React.FC<Props> = ({ open, onClose, hubId, rangeStart, rangeEnd }) => {
    const { t } = useTranslation();
    const [bucket, setBucket] = useState<InhubBaggingDestBucket>('HUB');

    const { data, loading } = useQuery<GetInhubSortingBaggingDestDrilldownResponse>(
        GET_INHUB_SORTING_BAGGING_DEST_DRILLDOWN,
        {
            variables: { filter: { hubId, rangeStart, rangeEnd } },
            skip: !open,
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true
        }
    );

    const drilldown = data?.getInhubSortingBaggingDestDrilldown;
    const hubTotal = Number(drilldown?.hubTotal ?? 0);
    const whsTotal = Number(drilldown?.whsTotal ?? 0);
    const total = hubTotal + whsTotal;
    const hubPercent = total > 0 ? Math.round((hubTotal / total) * 100) : 0;
    const whsPercent = total > 0 ? Math.round((whsTotal / total) * 100) : 0;

    const rows = bucket === 'HUB' ? drilldown?.hubRows ?? [] : drilldown?.whsRows ?? [];
    const bucketTotal = bucket === 'HUB' ? hubTotal : whsTotal;
    const barColor = bucket === 'HUB' ? HUB_BAR_COLOR : WHS_BAR_COLOR;
    const labelColor = bucket === 'HUB' ? HUB_LABEL_COLOR : WHS_LABEL_COLOR;

    const chartData = useMemo(
        () =>
            rows.map((r) => ({
                destName: r.targetName,
                count: r.baggingCount,
                percent: bucketTotal > 0 ? Math.round((r.baggingCount / bucketTotal) * 100) : 0
            })),
        [rows, bucketTotal]
    );

    if (!open) return null;

    return (
        <div className="border border-gray-200 rounded-lg bg-white">
            <div className="flex items-center justify-between px-4 py-3 bg-purple-50 border-b border-purple-100 rounded-t-lg">
                <div className="flex items-center gap-2 text-sm text-purple-600">
                    <BarChartOutlined />
                    <span className="font-medium">{t(`${I18N}.drilldownTitle`)}</span>
                    <span className="text-gray-500 ml-2">
                        {(t(`${I18N}.drilldownTotal`) as string).replace('{{count}}', total.toLocaleString())}
                    </span>
                </div>
                <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
            </div>

            <div className="px-4 py-2 bg-gray-50 text-xs text-gray-700 flex items-center gap-3">
                <span>
                    {t(`${I18N}.destBucket.hub`)}{' '}
                    <span className="font-medium text-gray-900">
                        {hubTotal.toLocaleString()}{t('wms.delivery.inhubDashboard.unit.piece')}
                    </span>{' '}
                    ({hubPercent}%)
                </span>
                <span className="text-gray-300">·</span>
                <span>
                    {t(`${I18N}.destBucket.whs`)}{' '}
                    <span className="font-medium text-gray-900">
                        {whsTotal.toLocaleString()}{t('wms.delivery.inhubDashboard.unit.piece')}
                    </span>{' '}
                    ({whsPercent}%)
                </span>
            </div>

            <div className="px-4 pt-3">
                <Radio.Group value={bucket} onChange={(e) => setBucket(e.target.value)} size="middle">
                    <Radio.Button value="HUB">
                        <span className="inline-block w-2 h-2 rounded-full bg-purple-400 mr-1.5 align-middle" />
                        {t(`${I18N}.destBucket.hub`)}
                    </Radio.Button>
                    <Radio.Button value="WHS">
                        <span className="inline-block w-2 h-2 rounded-full bg-green-400 mr-1.5 align-middle" />
                        {t(`${I18N}.destBucket.whs`)}
                    </Radio.Button>
                </Radio.Group>
            </div>

            <div className="px-4 pb-4 pt-2">
                {loading && chartData.length === 0 ? (
                    <Skeleton active paragraph={{ rows: 3 }} />
                ) : (
                    <div style={{ height: Math.max(60, chartData.length * 38) }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 60, left: 8, bottom: 8 }}>
                                <XAxis type="number" hide />
                                <YAxis dataKey="destName" type="category" tick={{ fontSize: 12, fill: '#262626' }} width={70} />
                                <Tooltip cursor={{ fill: '#fafafa' }} />
                                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                                    {chartData.map((_, idx) => (
                                        <Cell key={idx} fill={barColor} />
                                    ))}
                                    <LabelList
                                        dataKey="count"
                                        position="insideLeft"
                                        style={{ fontSize: 12, fill: labelColor, fontWeight: 500 }}
                                        formatter={(v) => (v == null ? '' : Number(v).toLocaleString())}
                                    />
                                    <LabelList
                                        dataKey="percent"
                                        position="right"
                                        style={{ fontSize: 12, fill: '#595959' }}
                                        formatter={(v) => (typeof v === 'number' ? `${v}%` : String(v ?? ''))}
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BaggingDrilldownPanel;
